<template>
    <div class="my-2">
        <div class="xl:flex sm:justify-between bg-minplan-beige text-minplan-blue">

            <div class="xl:w-4/12 p-4 flex w-full xl:border-r">
                <div>
                    <InformationCircleIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                </div>
                <div>
                    <span class="truncate inline-block w-full font-bold">{{ $t("topics.info") }}</span><br>
                    <p class="text-xs">{{ topic.description }}</p>
                    <p class="text-xs" v-if="!topic.description">{{ $t("topics.emptyInfo") }}</p>
                </div>
            </div>

            <div class="xl:w-8/12 flex border-t xl:border-0">
                <div class="p-4 flex w-full">
                    <div class="xl:pl-2">
                        <TruckIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        <button type="button"
                                v-if="editSupplierLink"
                                @click="editSupplier">
                            <PencilIcon
                                class="mr-2 mt-2 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>

                    </div>
                    <div>
                        <span class="truncate inline-block w-full font-bold">{{ $t("topics.supplier") }}</span><br>
                        <span class="text-xs" v-if="!supplier">{{ $t("topics.emptySupplier") }}</span>

                        <!-- ANFORDERUNGEN-V2 V2-09, Bestandteil 1: der
                             Firmenname, als weiterführend gekennzeichnet.
                             Klick öffnet die Detailseite, Überfahren und
                             Tastaturfokus zeigen die Kurzinfo. Die Adresse
                             steht bewusst NICHT als eigene Zeile im Block —
                             der Kopf beantwortet nur, wen man zu diesem Thema
                             erreicht. -->
                        <supplier-summary-card
                            v-if="supplier"
                            :supplier="supplier"
                            :show-link="supplierShowLink"
                        />

                        <!-- V2-09, Bestandteile 2 bis 5: Beschriftung
                             "Ansprechperson" (klein und gedämpft), Name,
                             E-Mail mit Briefsymbol, Telefon mit
                             Mobiltelefon-Symbol. Die Symbole sind dieselben,
                             die im Kopf der Wohnungs-Übersicht bei der
                             Käuferbetreuung verwendet werden — es werden keine
                             neuen eingeführt.

                             Thema 9 (Welle 4, Paket c): trägt das Thema eine
                             ausgewählte Kontaktperson (`supplierContact`,
                             `TopicShowResource`), zeigt der Header SIE samt
                             Firmendaten-Fallback statt der Legacy-Felder
                             (`supplier.contact_*`) — sonst Bestand.
                             R3 Punkt 2: `resolvedEmail`/`resolvedPhone`
                             (vorher `contactEmail`/`contactPhone`). Der
                             kursive Firmendaten-Hinweis bleibt unverändert
                             und steht weiterhin direkt hinter der betroffenen
                             Angabe. -->
                        <p v-if="supplierContact" class="text-minplan-darkbeige text-sm leading-5 mt-1">
                            <span class="block text-xs uppercase tracking-wide text-minplan-darkbeige/80">
                                {{ $t("suppliers.contactPersonHeading") }}
                            </span>
                            <span class="block">{{ supplierContact.name }}</span>
                            <span v-if="supplierContact.resolvedEmail" class="flex items-center gap-1">
                                <MailIcon class="h-4 w-4 flex-shrink-0" />
                                {{ supplierContact.resolvedEmail }}
                                <span v-if="supplierContact.emailIsFallback" class="italic text-gray-400">
                                    ({{ $t("suppliers.contactFallbackNotice") }})
                                </span>
                            </span>
                            <span v-if="supplierContact.resolvedPhone" class="flex items-center gap-1">
                                <DeviceMobileIcon class="h-4 w-4 flex-shrink-0" />
                                {{ supplierContact.resolvedPhone }}
                                <span v-if="supplierContact.phoneIsFallback" class="italic text-gray-400">
                                    ({{ $t("suppliers.contactFallbackNotice") }})
                                </span>
                            </span>
                        </p>
                        <p v-else-if="supplier" class="text-minplan-darkbeige text-sm leading-5 mt-1">
                            <span class="block text-xs uppercase tracking-wide text-minplan-darkbeige/80">
                                {{ $t("suppliers.contactPersonHeading") }}
                            </span>
                            <span class="block">
                                {{ supplier.contact_firstname }} {{ supplier.contact_lastname }}
                            </span>
                            <span v-if="supplier.contact_email" class="flex items-center gap-1">
                                <MailIcon class="h-4 w-4 flex-shrink-0" />
                                {{ supplier.contact_email }}
                            </span>
                            <span v-if="supplier.contact_mobile" class="flex items-center gap-1">
                                <DeviceMobileIcon class="h-4 w-4 flex-shrink-0" />
                                {{ supplier.contact_mobile }}
                            </span>
                        </p>
                    </div>
                </div>
                <div class="md:flex w-full">
                    <div class="p-4 flex w-full">
                        <div>
                            <CurrencyDollarIcon class="mr-2 h-5 w-5 text-minplan-blue" />
                            <button type="button"
                                    v-if="editBudgetLink"
                                    @click="editBudget">
                                <PencilIcon
                                    class="mr-2 mt-2 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                            </button>
                        </div>
                        <!-- Entscheid 13 (Auftrag 2026-08-16): Ist die
                             Budget-Funktion für diese Zielgruppe ausgeblendet
                             (nur möglich im Offerten-Modus), heisst der Block
                             "Inklusivbudget", zeigt den effektiven Wert wie
                             bisher und im Leerfall "ohne Inklusivbudget". Die
                             Budget-Zahl erscheint dann gar nicht mehr —
                             erfasste Beträge bleiben aber gespeichert
                             (Entscheid 16). -->
                        <div v-if="showBudget">
                                <span class="truncate inline-block w-full font-bold">
                                    {{ $t("topics.budget") }}
                                </span>
                            <br>
                            <span class="text-sm">{{ topic.formattedBudget }}</span><br />

                            <span v-if="topic.inclusive_unformattedBudget > 0" class="text-xs text-minplan-darkbeige">
                                {{ $t("topics.includedInPurchasePrice") }}<br />
                                {{ topic.inclusive_budget }}
                                <span v-if="topic.inclusive_perUnit != null" class="ml-1 text-minplan-darkbeige/80">
                                    <br />&#126; {{ topic.inclusive_perUnit }}
                                </span>
                            </span>
                        </div>
                        <div v-else data-test="inclusive-budget-only">
                                <span class="truncate inline-block w-full font-bold">
                                    {{ $t("topics.inclusiveBudget") }}
                                </span>
                            <br>
                            <span v-if="topic.inclusive_unformattedBudget > 0" class="text-sm">
                                {{ topic.inclusive_budget }}
                                <span v-if="topic.inclusive_perUnit != null" class="ml-1 text-xs text-minplan-darkbeige/80">
                                    <br />&#126; {{ topic.inclusive_perUnit }}
                                </span>
                            </span>
                            <span v-else class="text-xs">{{ $t("topics.withoutInclusiveBudget") }}</span>
                        </div>
                    </div>
                    <div class="p-4 flex w-full">
                        <div>
                            <CalendarIcon class="mr-2 h-5 w-5 text-minplan-blue" />
                            <button type="button"
                                    v-if="editDeadlineLink"
                                    @click="editDeadline">
                                <PencilIcon
                                    class="mr-2 mt-2 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                            </button>
                        </div>
                        <div>
                                <span class="truncate inline-block w-full font-bold">
                                    {{ $t("topics.deadline") }}
                                </span>
                            <br>
                            <span class="text-xs" v-if="!topic.deadline">{{ $t("topics.emptyDeadline") }}</span>
                            <span class="text-sm">{{ topic.deadline }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import {
    CalendarIcon,
    CurrencyDollarIcon,
    DeviceMobileIcon,
    InformationCircleIcon,
    MailIcon,
    PencilIcon,
    TruckIcon
} from "@heroicons/vue/outline";
import SupplierSummaryCard from "@/Shared/Components/Suppliers/SupplierSummaryCard";

export default {
    // Auftrag 2026-08-16, Entscheid 13: Objekt- statt Array-Form. `showBudget`
    // braucht einen Boolean-Default `true`, damit jede Einbindung ohne die
    // Prop unverändert bleibt — die Array-Form kennt keine Defaults. Die
    // übrigen Props behalten ihre bisherige, typlose Deklaration.
    props: {
        supplier: {},
        supplierContact: {},
        // ANFORDERUNGEN-V2 V2-09: Ziel des Firmennamen-Links. Kommt als Prop
        // von der Page (NORM, CONVENTIONS-IST 2.4) und fehlt in der
        // Käuferansicht — dort gibt es keine Unternehmer-Detailseite.
        supplierShowLink: {},
        topic: {},
        editDeadlineLink: {},
        editSupplierLink: {},
        editBudgetLink: {},
        showBudget: { type: Boolean, default: true }
    },
    components: {
        PencilIcon,
        CurrencyDollarIcon,
        TruckIcon,
        CalendarIcon,
        InformationCircleIcon,
        MailIcon,
        DeviceMobileIcon,
        SupplierSummaryCard
    },
    methods: {
        editSupplier(){
            this.$inertia.visitInModal(this.editSupplierLink)
        },
        editBudget(){
            this.$inertia.visitInModal(this.editBudgetLink)
        },
        editDeadline(){
            this.$inertia.visitInModal(this.editDeadlineLink)
        }
    }
};
</script>

<style scoped>

</style>
